import { type FormEvent, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import * as api from '../api';
import Header from '../components/Header';
import { useSettingsStore } from '../stores/settingsStore';

export default function SettingsPage() {
  const sources = useSettingsStore((s) => s.sources);
  const hiddenIds = useSettingsStore((s) => s.hiddenIds);
  const interestText = useSettingsStore((s) => s.interestText);
  const classifierRoles = useSettingsStore((s) => s.classifierRoles);
  const fetchSources = useSettingsStore((s) => s.fetchSources);
  const saveSettings = useSettingsStore((s) => s.saveSettings);

  const [enabled, setEnabled] = useState<Set<number>>(new Set());
  const [localInterestText, setLocalInterestText] = useState('');
  const [localRoles, setLocalRoles] = useState<
    Record<number, 'interest' | 'no-interest' | 'hide' | null>
  >({});
  const [classifiers, setClassifiers] = useState<
    { id: number; name: string }[]
  >([]);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const savedTimer = useRef<ReturnType<typeof setTimeout>>();

  // Auto-classifier creation state
  const [newConcept, setNewConcept] = useState('');
  const [creating, setCreating] = useState(false);
  const [createProgress, setCreateProgress] = useState('');
  const [createError, setCreateError] = useState('');

  useEffect(() => {
    fetchSources();
    api.fetchClassifiers().then((data) => setClassifiers(data.classifiers));
    return () => clearTimeout(savedTimer.current);
  }, [fetchSources]);

  useEffect(() => {
    const enabledIds = sources
      .filter((s) => !hiddenIds.includes(s.id))
      .map((s) => s.id);
    setEnabled(new Set(enabledIds));
  }, [sources, hiddenIds]);

  useEffect(() => {
    setLocalInterestText(interestText);
  }, [interestText]);

  useEffect(() => {
    const roles: Record<number, 'interest' | 'no-interest' | null> = {};
    for (const c of classifiers) {
      roles[c.id] = classifierRoles[c.id] ?? null;
    }
    setLocalRoles(roles);
  }, [classifiers, classifierRoles]);

  const toggle = (id: number) => {
    setEnabled((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const hasAnyRole = Object.values(localRoles).some(
    (v) => v === 'interest' || v === 'no-interest' || v === 'hide',
  );

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSaving(true);
    if (hasAnyRole) {
      await saveSettings([...enabled], '', localRoles);
    } else {
      await saveSettings([...enabled], localInterestText);
    }
    setSaving(false);
    setSaved(true);
    clearTimeout(savedTimer.current);
    savedTimer.current = setTimeout(() => setSaved(false), 2000);
  };

  const handleRoleChange = (
    classifierId: number,
    value: 'interest' | 'no-interest' | 'hide' | '',
  ) => {
    setLocalRoles((prev) => ({
      ...prev,
      [classifierId]: value === '' ? null : value,
    }));
    // If setting a classifier role, clear interest text
    if (value !== '') {
      setLocalInterestText('');
    }
  };

  const handleInterestTextChange = (value: string) => {
    setLocalInterestText(value);
    // If typing interest text, clear classifier roles
    if (value.trim().length > 0) {
      setLocalRoles((prev) => {
        const cleared: typeof prev = {};
        for (const id of Object.keys(prev)) {
          cleared[Number(id)] = null;
        }
        return cleared;
      });
    }
  };

  const handleDeleteClassifier = async (classifierId: number) => {
    await api.deleteClassifier(classifierId);
    setClassifiers((prev) => prev.filter((c) => c.id !== classifierId));
    setLocalRoles((prev) => {
      const next = { ...prev };
      delete next[classifierId];
      return next;
    });
  };

  const handleCreateClassifier = async () => {
    const concept = newConcept.trim();
    if (!concept || creating) return;
    setCreating(true);
    setCreateProgress('Starting…');
    setCreateError('');
    try {
      const { classifierId } = await api.autoClassifyStream(
        `auto ${concept}`,
        concept,
        (event) => {
          if (event.message) setCreateProgress(event.message);
        },
      );
      // Add the new classifier to the list
      setClassifiers((prev) => [
        ...prev,
        { id: classifierId, name: `auto ${concept}` },
      ]);
      setLocalRoles((prev) => ({ ...prev, [classifierId]: null }));
      setNewConcept('');
      setCreateProgress('');
    } catch (err) {
      setCreateError(err instanceof Error ? err.message : String(err));
      setCreateProgress('');
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="page-settings">
      <Header />
      <Link className="back-link" to="/">
        &larr; Back to headlines
      </Link>

      <h2>Sources</h2>
      <p className="settings-desc">
        Choose which sources appear in the navigation and the All view.
      </p>

      <form onSubmit={handleSubmit}>
        <ul className="source-list">
          {sources.map((s) => (
            <li key={s.id}>
              <label>
                <input
                  type="checkbox"
                  checked={enabled.has(s.id)}
                  onChange={() => toggle(s.id)}
                />
                {s.name}
              </label>
            </li>
          ))}
        </ul>

        <h2>Interest Matching</h2>
        <p className="settings-desc">
          You can use either free-text interests or classifier-based interests,
          not both. Configuring one will disable the other.
        </p>

        <h3>Free-text interests</h3>
        <p className="settings-desc">
          Describe your interests in free text. Headlines will be checked
          against your interests and marked with [+] or [-].
        </p>
        <textarea
          className="interest-textarea"
          rows={4}
          placeholder="e.g. AI and machine learning, climate policy, European politics"
          value={localInterestText}
          onChange={(e) => handleInterestTextChange(e.target.value)}
          disabled={hasAnyRole}
        />

        <h3>Classifier-based interests</h3>
        <p className="settings-desc">
          Assign classifiers as interest (+) or no-interest (-). Headlines
          accepted by these classifiers will be marked accordingly.
        </p>
        {classifiers.length > 0 && (
          <ul className="classifier-role-list">
            {classifiers.map((c) => (
              <li key={c.id}>
                <span className="classifier-role-name">{c.name}</span>
                <select
                  value={localRoles[c.id] ?? ''}
                  onChange={(e) =>
                    handleRoleChange(
                      c.id,
                      e.target.value as
                        | 'interest'
                        | 'no-interest'
                        | 'hide'
                        | '',
                    )
                  }
                >
                  <option value="">None</option>
                  <option value="interest">Interest (+)</option>
                  <option value="no-interest">No interest (-)</option>
                  <option value="hide">Hide</option>
                </select>
                <button
                  type="button"
                  className="classifier-delete-btn"
                  onClick={() => handleDeleteClassifier(c.id)}
                >
                  delete
                </button>
              </li>
            ))}
          </ul>
        )}

        <div className="classifier-create-inline">
          <input
            type="text"
            className="classifier-create-input"
            placeholder="e.g. space exploration, trade wars"
            value={newConcept}
            onChange={(e) => setNewConcept(e.target.value)}
            disabled={creating}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleCreateClassifier();
              }
            }}
          />
          <button
            type="button"
            className="classifier-create-btn"
            onClick={handleCreateClassifier}
            disabled={creating || !newConcept.trim()}
          >
            {creating ? 'Creating…' : 'Add classifier'}
          </button>
        </div>
        {createProgress && (
          <p className="classifier-create-progress">{createProgress}</p>
        )}
        {createError && (
          <p className="classifier-create-error">{createError}</p>
        )}

        <button type="submit" className="save-btn" disabled={saving}>
          {saving ? 'Saving…' : 'Save'}
        </button>
        {saved && <span className="saved-indicator">Saved</span>}
      </form>
    </div>
  );
}
